import * as React from "react";

import { TimeDisplayMode } from "@/shared/components/visualization/WorkspaceCtx/TimeDisplayMode";
import { CheckIcon } from "@/shared/foundation/icons/mui";
import { dateToNanosecString, nanosecStringToDate } from "@/shared/time";

import {
  buildDate,
  daysInMonth,
  firstDayOfWeek,
  getDateOfMonth,
  getHours,
  getMinutes,
  getMonth,
  getSeconds,
  getYear,
  to24h,
} from "./dateTimeHelpers";
import styles from "./DateTimeSelector.module.css";

export interface DateTimeSelectorProps {
  /** Current value as a nanosecond epoch string, or "" if empty */
  value: string;
  /** Called when the user selects or changes a date/time */
  onChange: (nanosecEpochString: string) => void;
  /** Determines timezone interpretation and 12h vs 24h display */
  timeDisplayMode: TimeDisplayMode;
  /** Optional label (e.g., "From", "To") */
  label?: string;
  /** When provided, renders a green checkmark button that calls this callback. */
  onConfirm?: () => void;
}

const DAY_LABELS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"] as const;

const MONTH_LABELS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
] as const;

// ── Component ───────────────────────────────────────────────────────

export function DateTimeSelector({
  value,
  onChange,
  timeDisplayMode,
  label,
  onConfirm,
}: DateTimeSelectorProps) {
  const utc = timeDisplayMode === TimeDisplayMode.UTC;
  const is12h = !utc;

  // Parse value or fall back to a stable "now" snapshot for display-only
  // purposes. The fallback is initialized once on mount so the rendered
  // calendar is stable across renders; it is never emitted, so opening the
  // picker does not create a filter until the user selects a date/time.
  const [fallbackDate] = React.useState(() => new Date());
  const parsed = nanosecStringToDate(value);
  const date = parsed ?? fallbackDate;

  // Emit change
  const emit = React.useCallback(
    (next: Date) => {
      onChange(dateToNanosecString(next));
    },
    [onChange],
  );

  // Decompose date
  const year = getYear(date, utc);
  const month = getMonth(date, utc);
  const day = getDateOfMonth(date, utc);
  const hours24 = getHours(date, utc);
  const minutes = getMinutes(date, utc);
  const seconds = getSeconds(date, utc);

  // 12h conversion
  const isPm = hours24 >= 12;
  const displayHour = is12h ? hours24 % 12 || 12 : hours24;

  // ── Year/month navigation ──

  const navigateMonth = React.useCallback(
    (delta: 1 | -1) => {
      let newMonth = month + delta;
      let newYear = year;
      if (newMonth < 0) {
        newMonth = 11;
        newYear -= 1;
      } else if (newMonth > 11) {
        newMonth = 0;
        newYear += 1;
      }
      const maxDay = daysInMonth(newYear, newMonth, utc);
      const clampedDay = Math.min(day, maxDay);
      emit(
        buildDate(
          newYear,
          newMonth,
          clampedDay,
          hours24,
          minutes,
          seconds,
          utc,
        ),
      );
    },
    [year, month, day, hours24, minutes, seconds, utc, emit],
  );

  // ── Calendar grid ──

  const totalDays = daysInMonth(year, month, utc);
  const startDow = firstDayOfWeek(year, month, utc);
  const prevYear = month - 1 < 0 ? year - 1 : year;
  const prevMonth = month - 1 < 0 ? 11 : month - 1;
  const prevMonthDays = daysInMonth(prevYear, prevMonth, utc);

  const cells: { day: number; monthOffset: -1 | 0 | 1 }[] = [];
  // Fill leading days from previous month
  for (let i = startDow - 1; i >= 0; i--) {
    cells.push({ day: prevMonthDays - i, monthOffset: -1 });
  }
  // Current month days
  for (let d = 1; d <= totalDays; d++) {
    cells.push({ day: d, monthOffset: 0 });
  }
  // Fill trailing days from next month
  const remaining = 7 - (cells.length % 7);
  if (remaining < 7) {
    for (let d = 1; d <= remaining; d++) {
      cells.push({ day: d, monthOffset: 1 });
    }
  }

  const handleDayClick = React.useCallback(
    (clickedDay: number, monthOffset: -1 | 0 | 1) => {
      let newYear = year;
      let newMonth = month + monthOffset;
      if (newMonth < 0) {
        newMonth = 11;
        newYear -= 1;
      } else if (newMonth > 11) {
        newMonth = 0;
        newYear += 1;
      }
      emit(
        buildDate(
          newYear,
          newMonth,
          clickedDay,
          hours24,
          minutes,
          seconds,
          utc,
        ),
      );
    },
    [year, month, hours24, minutes, seconds, utc, emit],
  );

  // ── Time input helpers ──

  const updateTime = React.useCallback(
    (newHours24: number, newMinutes: number, newSeconds: number) => {
      emit(
        buildDate(year, month, day, newHours24, newMinutes, newSeconds, utc),
      );
    },
    [year, month, day, utc, emit],
  );

  const handleHourBlur = React.useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      const raw = parseInt(e.target.value, 10);
      if (Number.isNaN(raw)) {
        return;
      }
      let h24: number;
      if (is12h) {
        const clamped = Math.max(1, Math.min(12, raw));
        h24 = to24h(clamped, isPm);
      } else {
        h24 = Math.max(0, Math.min(23, raw));
      }
      updateTime(h24, minutes, seconds);
    },
    [is12h, isPm, minutes, seconds, updateTime],
  );

  const handleMinuteBlur = React.useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      const raw = parseInt(e.target.value, 10);
      if (Number.isNaN(raw)) {
        return;
      }
      updateTime(hours24, Math.max(0, Math.min(59, raw)), seconds);
    },
    [hours24, seconds, updateTime],
  );

  const handleSecondBlur = React.useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      const raw = parseInt(e.target.value, 10);
      if (Number.isNaN(raw)) {
        return;
      }
      updateTime(hours24, minutes, Math.max(0, Math.min(59, raw)));
    },
    [hours24, minutes, updateTime],
  );

  const handleAmPm = React.useCallback(
    (wantPm: boolean) => {
      if (wantPm === isPm) {
        return;
      }
      const raw = wantPm ? hours24 + 12 : hours24 - 12;
      const newH24 = Math.max(0, Math.min(23, raw));
      updateTime(newH24, minutes, seconds);
    },
    [isPm, hours24, minutes, seconds, updateTime],
  );

  const handleTimeKeyDown = React.useCallback(
    (
      e: React.KeyboardEvent<HTMLInputElement>,
      field: "hour" | "minute" | "second",
    ) => {
      if (e.key !== "ArrowUp" && e.key !== "ArrowDown") {
        return;
      }
      e.preventDefault();
      const delta = e.key === "ArrowUp" ? 1 : -1;
      if (field === "hour") {
        if (is12h) {
          const next12 = ((displayHour - 1 + delta + 12) % 12) + 1;
          const h24 = to24h(next12, isPm);
          updateTime(h24, minutes, seconds);
        } else {
          updateTime((hours24 + delta + 24) % 24, minutes, seconds);
        }
      } else if (field === "minute") {
        updateTime(hours24, (minutes + delta + 60) % 60, seconds);
      } else {
        updateTime(hours24, minutes, (seconds + delta + 60) % 60);
      }
    },
    [is12h, displayHour, isPm, hours24, minutes, seconds, updateTime],
  );

  // Year range for dropdown
  const yearOptions = React.useMemo(() => {
    const result: number[] = [];
    for (let y = year - 10; y <= year + 10; y++) {
      result.push(y);
    }
    return result;
  }, [year]);

  return (
    <div className={styles.container}>
      {label !== undefined && <span className={styles.label}>{label}</span>}

      {/* ── Date section ── */}
      <div className={styles.dateSection}>
        <div className={styles.headerRow}>
          <button
            type="button"
            className={styles.navButton}
            onClick={() => navigateMonth(-1)}
            aria-label="Previous month"
          >
            &lsaquo;
          </button>
          <select
            className={styles.headerSelect}
            value={month}
            onChange={(e) => {
              const newMonth = parseInt(e.target.value, 10);
              const maxDay = daysInMonth(year, newMonth, utc);
              const clampedDay = Math.min(day, maxDay);
              emit(
                buildDate(
                  year,
                  newMonth,
                  clampedDay,
                  hours24,
                  minutes,
                  seconds,
                  utc,
                ),
              );
            }}
          >
            {MONTH_LABELS.map((lbl, i) => (
              <option key={i} value={i}>
                {lbl}
              </option>
            ))}
          </select>
          <select
            className={styles.headerSelect}
            value={year}
            onChange={(e) => {
              const newYear = parseInt(e.target.value, 10);
              const maxDay = daysInMonth(newYear, month, utc);
              const clampedDay = Math.min(day, maxDay);
              emit(
                buildDate(
                  newYear,
                  month,
                  clampedDay,
                  hours24,
                  minutes,
                  seconds,
                  utc,
                ),
              );
            }}
          >
            {yearOptions.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
          <div className={styles.headerSpacer} />
          <button
            type="button"
            className={styles.navButton}
            onClick={() => navigateMonth(1)}
            aria-label="Next month"
          >
            &rsaquo;
          </button>
        </div>

        {/* Day-of-week headers */}
        <div className={styles.dayGrid}>
          {DAY_LABELS.map((lbl) => (
            <span key={lbl} className={styles.dayHeader}>
              {lbl}
            </span>
          ))}

          {/* Calendar day cells */}
          {cells.map((cell, idx) => (
            <button
              key={idx}
              type="button"
              className={styles.dayButton}
              data-selected={cell.monthOffset === 0 && cell.day === day}
              data-adjacent={cell.monthOffset !== 0}
              onClick={() => handleDayClick(cell.day, cell.monthOffset)}
            >
              {cell.day}
            </button>
          ))}
        </div>
      </div>

      {/* ── Time section ── */}
      <div className={styles.timeSection}>
        <TimeInput
          value={displayHour}
          onBlur={handleHourBlur}
          onKeyDown={(e) => handleTimeKeyDown(e, "hour")}
          aria-label="Hours"
        />
        <span className={styles.timeSeparator}>:</span>
        <TimeInput
          value={minutes}
          onBlur={handleMinuteBlur}
          onKeyDown={(e) => handleTimeKeyDown(e, "minute")}
          aria-label="Minutes"
        />
        <span className={styles.timeSeparator}>:</span>
        <TimeInput
          value={seconds}
          onBlur={handleSecondBlur}
          onKeyDown={(e) => handleTimeKeyDown(e, "second")}
          aria-label="Seconds"
        />

        {is12h && (
          <div className={styles.amPmToggle}>
            <button
              type="button"
              className={styles.amPmButton}
              data-active={!isPm}
              onClick={() => handleAmPm(false)}
            >
              AM
            </button>
            <button
              type="button"
              className={styles.amPmButton}
              data-active={isPm}
              onClick={() => handleAmPm(true)}
            >
              PM
            </button>
          </div>
        )}
      </div>

      {onConfirm !== undefined && (
        <div className={styles.confirmRow}>
          <button
            type="button"
            className={styles.confirmButton}
            onClick={onConfirm}
            aria-label="Confirm date/time"
            title="Confirm date/time"
          >
            <CheckIcon fontSize="small" />
          </button>
        </div>
      )}
    </div>
  );
}

// ── Reusable time input ──────────────────────────────────────────────

const TimeInput = React.memo(function TimeInput({
  value,
  onBlur,
  onKeyDown,
  "aria-label": ariaLabel,
}: {
  value: number;
  onBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  "aria-label": string;
}) {
  const [text, setText] = React.useState(String(value).padStart(2, "0"));
  const isFocusedRef = React.useRef(false);

  // Sync external value changes only when the input is not focused,
  // so we don't overwrite partially typed text.
  React.useEffect(() => {
    if (!isFocusedRef.current) {
      setText(String(value).padStart(2, "0"));
    }
  }, [value]);

  return (
    <input
      type="text"
      inputMode="numeric"
      maxLength={2}
      className={styles.timeInput}
      value={text}
      aria-label={ariaLabel}
      onFocus={() => {
        isFocusedRef.current = true;
      }}
      onChange={(e) => {
        // Allow only digits
        const filtered = e.target.value.replace(/\D/g, "");
        setText(filtered);
      }}
      onBlur={(e) => {
        isFocusedRef.current = false;
        // Pad on blur
        const padded = e.target.value.padStart(2, "0");
        setText(padded);
        onBlur(e);
      }}
      onKeyDown={onKeyDown}
    />
  );
});
